import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/question/forms/shared/Answer';
import { QuizAnswerCreateDTO } from '@/models/Answer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type QuizAnswerMediaProps = {
  answer: QuizAnswerCreateDTO;
  isEditing: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
  onUpdateMedia: (file: File | string, altText?: string) => void;
  onSwitchToText: () => void;
  onToggleCorrect: () => void;
  onDelete: () => void;
  isLoading?: boolean;
};

export default function QuizAnswerMedia({
  answer,
  isEditing,
  onStartEdit,
  onFinishEdit,
  onUpdateMedia,
  onSwitchToText,
  onToggleCorrect,
  onDelete,
  isLoading = false,
}: QuizAnswerMediaProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.quizForm',
  });

  return (
    <Answer isCorrect={answer.isCorrect} isEditing={isEditing} isMedia>
      <ConditionalRenderer
        condition={isEditing}
        fallback={
          <Answer.MediaPreview
            mediaFile={answer.mediaFile}
            altText={answer.altText ?? undefined}
            placeholder={t('mediaPlaceholder')}
          />
        }
      >
        <Answer.MediaInput
          initialMediaFile={answer.mediaFile}
          initialAltText={answer.altText ?? ''}
          onConfirm={(file, altText) => {
            onUpdateMedia(file, altText);
            onFinishEdit();
          }}
          onCancel={onFinishEdit}
        />
      </ConditionalRenderer>
      <Answer.MoreOptions>
        <Answer.MoreOptions.EditText
          onClick={onSwitchToText}
          disabled={isLimitedEdition}
        />
        <Answer.MoreOptions.EditMedia
          onClick={onStartEdit}
          disabled={isLoading}
        />
        <Answer.MoreOptions.ToggleCorrect
          isCorrect={answer.isCorrect}
          onClick={onToggleCorrect}
          disabled={isLimitedEdition || isLoading}
        />
        <Answer.MoreOptions.Delete
          onClick={onDelete}
          disabled={isLimitedEdition || isLoading}
        />
      </Answer.MoreOptions>
    </Answer>
  );
}
